// Dashboard designer — canvas first (docs/plans/dashboard-designer-redesign.md).
//
// The dashboard is the page: its name, the run-time filter card and the tiles
// on the 12-column grid, where tiles are dragged, resized from any edge or
// corner, arranged automatically and found by name. Settings sit in a panel on
// the right (Layout · Global criteria · Drill-through · ⚙ Properties), the
// same shell as the visual designer. Adding a visual is a modal.
//
// What is saved is unchanged (DashboardModel, ./dashboard/types.ts) except
// DRILL_THROUGH[].SOURCE_COLUMN — a tile may now carry one link per column.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  ArrowLeft, Loader2, Save, Plus, Search, ChevronUp, ChevronDown, X, Settings,
  Maximize2, Minimize2, Eye, Pencil, LayoutGrid,
} from 'lucide-react'
import type { EmbeddedDesignerProps } from './embeddedDesigner'
import {
  loadDashboardObject, saveDashboardObject,
  listProjectVisuals, loadVisualObject, type ProjectVisualSummary,
  listProjectReports, loadReportObject, type ProjectReportSummary,
  listProjectDashboards, type ProjectDashboardSummary,
} from '@/api/designers'
import { useProjectStore } from '@/store/projectStore'
import { useBuilderContextStore } from '@/store/builderContextStore'
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard'
import { useAgentDeletion, useAgentRefresh } from '@/hooks/useAgentRefresh'
import { useDesignHistory } from '@/hooks/useDesignHistory'
import UndoRedoButtons from '@/components/common/UndoRedoButtons'
import DiscardChangesModal from '@/components/common/DiscardChangesModal'
import type {
  DashboardModel, DashboardBody, DashboardVisual,
  DashboardGlobalCriterion, DashboardDrillThrough, DashboardCriterionBinding,
} from './dashboard/types'
import {
  blankDashboardModel, sanitizeDashboardModel,
  blankVisual, blankCriterion, blankDrillThrough,
  syncBroadcastFromBindings,
} from './dashboard/helpers'
import DashboardPreviewModal from './dashboard/DashboardPreviewModal'
import { bustPreview } from '@/pages/workbench/analytics/PreviewThumbnail'
import { TypeChip } from '@/pages/workbench/analytics/shared'
import PublishToMenuDialog from '@/pages/workbench/analytics/PublishToMenuDialog'
import VisualDesignerPage from './VisualDesignerPage'
import { listProjectVariables, type ProjectVariable } from '@/api/projectVariables'
import { useProjectPermissions } from '@/auth/useProjectPermissions'
import { ReadOnlyBadge } from '@/auth/ReadOnlyBadge'
import { useRunOnlyRedirect } from '@/auth/useRunOnlyRedirect'
import { DASH_STEP } from '@twasta/visual-engine'
import { VDO_CSS } from './visual/designerShellCss'
import { useDragWidth } from './visual/useDragWidth'
import { DD_CSS } from './dashboard/designerCss'
import {
  arrangeTiles, bottomRow, moveTiles, removeBand, setTileBox, type ArrangeMode,
} from './dashboard/layoutOps'
import { tileContentKey, visualRefToObjName, type PanelTab } from './dashboard/designerShared'
import DesignCanvas from './dashboard/DesignCanvas'
import LayoutPanel from './dashboard/LayoutPanel'
import CriteriaPanel from './dashboard/CriteriaPanel'
import DrillPanel from './dashboard/DrillPanel'
import PropertiesPanel from './dashboard/PropertiesPanel'
import AddVisualModal, { type AddVisualPick, type TileContentKind } from './dashboard/AddVisualModal'

const ARRANGE: Array<{ mode: ArrangeMode; title: string; detail: string; icon: number[] }> = [
  { mode: 'gaps', title: 'Close gaps', detail: 'Moves tiles up into empty space. Sizes and columns stay the same.', icon: [2, 2, 3, 1] },
  { mode: 'rows', title: 'Tidy into rows', detail: 'Lines tiles up left to right, top to bottom, in their current order. Sizes stay the same.', icon: [1, 1, 2, 2, 2] },
  { mode: 'fill', title: 'Fill each row', detail: "Tidies into rows, then widens tiles so every row spans the full width and evens out each row's height.", icon: [2, 2, 4] },
]
const ARRANGE_DONE: Record<ArrangeMode, string> = {
  gaps: 'Gaps closed. Tiles moved up.',
  rows: 'Tiles tidied into rows.',
  fill: 'Rows filled to the full width.',
}

export default function DashboardDesignerPage({
  embedded = false,
  hostFullscreen = false,
  embedProject,
  embedObjName,
  onDirtyChange,
  registerSave,
  onSaved,
}: EmbeddedDesignerProps = {}) {
  const params = useParams<{ project: string; objName: string }>()
  const project = embedProject ?? params.project
  useRunOnlyRedirect(project)
  const objName = embedObjName ?? params.objName
  const navigate = useNavigate()
  const location = useLocation()
  const projectName = project || ''
  const objectName = objName || ''
  const { currentProject, selectProject } = useProjectStore()

  useEffect(() => {
    if (project && currentProject?.application.folder_name !== project) {
      selectProject(project)
    }
  }, [project, currentProject, selectProject])
  const appColor = currentProject?.application.folder_name === projectName
    ? currentProject.application.theme_color || null
    : null

  const [model, setModel] = useState<DashboardModel>(() => blankDashboardModel(objectName))
  const [projectVisuals, setProjectVisuals] = useState<ProjectVisualSummary[]>([])
  const [projectDashboards, setProjectDashboards] = useState<ProjectDashboardSummary[]>([])
  const [projectReports, setProjectReports] = useState<ProjectReportSummary[]>([])
  // Loaded visual / report models keyed by tileContentKey — the bindings UI,
  // the tile footers and the drill-through column picker read them. A ref that
  // failed to load is stored as null so it is not refetched.
  const [visualContents, setVisualContents] = useState<Record<string, unknown | null>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const { canEditObjectsOrLoading: canEdit } = useProjectPermissions()
  const [embedFullscreen, setEmbedFullscreen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [editVisualObj, setEditVisualObj] = useState<string | null>(null)

  const [tab, setTab] = useState<PanelTab>('layout')
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set())
  const [primaryId, setPrimaryId] = useState<string | null>(null)
  // Global criteria / drill-through cards: which are open, which was touched last.
  const [gOpen, setGOpen] = useState<ReadonlySet<number>>(new Set())
  const [gFocus, setGFocus] = useState<number | null>(null)
  const [lOpen, setLOpen] = useState<ReadonlySet<number>>(new Set())
  const [lFocus, setLFocus] = useState<number | null>(null)
  const [modal, setModal] = useState<{ mode: 'add' | 'replace'; kind: TileContentKind } | null>(null)
  const [arrangeOpen, setArrangeOpen] = useState(false)
  const arrangeRef = useRef<HTMLDivElement>(null)
  const [findQ, setFindQ] = useState('')
  const [findI, setFindI] = useState(-1)
  const findRef = useRef<HTMLInputElement>(null)
  const [undoNote, setUndoNote] = useState<string | null>(null)
  // Bumped when a tile's visual was edited in the overlay: the canvas re-runs.
  const [dataVersion, setDataVersion] = useState(0)

  const [projectVars, setProjectVars] = useState<ProjectVariable[]>([])
  useEffect(() => {
    if (!projectName) { setProjectVars([]); return }
    let cancelled = false
    listProjectVariables(projectName)
      .then((vars) => { if (!cancelled) setProjectVars(vars) })
      .catch((err) => { console.warn('Could not list project variables', err); if (!cancelled) setProjectVars([]) })
    return () => { cancelled = true }
  }, [projectName])

  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null)
  const isDirty = savedSnapshot !== null && JSON.stringify(model) !== savedSnapshot
  const { guard, showDiscardModal, confirmDiscard, cancelDiscard } = useUnsavedChangesGuard(isDirty)
  const history = useDesignHistory(model, setModel, { disabled: !canEdit })
  const { reset: resetHistory } = history

  // Reload when an AI agent run touches project files (deferred while dirty).
  const agentRefresh = useAgentRefresh(projectName, isDirty)
  // An agent run that DELETED this object closes it rather than refetching.
  useAgentDeletion(projectName, objectName, 'dashboard_models', () => {
    if (embedded) return
    navigate('/workbench/analytics')
  })

  // The open dashboard is the Builder / Ask-AI "open object".
  useEffect(() => {
    if (!objectName) return
    useBuilderContextStore.getState().setOpenObject({ obj_name: objectName, obj_type: 'D', title: objectName })
    return () => useBuilderContextStore.getState().setOpenObject(null)
  }, [objectName])

  // Load the dashboard and the project's visuals / dashboards / reports.
  useEffect(() => {
    let cancelled = false
    if (!projectName || !objectName) return
    setLoading(true)
    Promise.all([
      loadDashboardObject(projectName, objectName).catch((err) => {
        console.error('Failed to load dashboard:', err)
        toast.error('Failed to load dashboard')
        return null
      }),
      listProjectVisuals(projectName).catch((err) => { console.warn('list visuals failed', err); return null }),
      listProjectDashboards(projectName).catch((err) => { console.warn('list dashboards failed', err); return null }),
      listProjectReports(projectName).catch((err) => { console.warn('list reports failed', err); return null }),
    ])
      .then(([data, visuals, dashboards, reports]) => {
        if (cancelled) return
        const loaded = data && data.content
          ? sanitizeDashboardModel(data.content, objectName)
          : blankDashboardModel(objectName)
        setModel(loaded)
        setSavedSnapshot(JSON.stringify(loaded))
        resetHistory()
        if (visuals && visuals.success) setProjectVisuals(visuals.visuals || [])
        if (dashboards && dashboards.success) setProjectDashboards(dashboards.dashboards || [])
        if (reports && reports.success) setProjectReports(reports.reports || [])
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [projectName, objectName, agentRefresh, resetHistory])

  // Load each tile's visual / report model once (cache by tileContentKey).
  useEffect(() => {
    if (!projectName) return
    const refs = Array.from(new Set(model.DASHBOARD.VISUALS.map(tileContentKey).filter(Boolean)))
    const missing = refs.filter((r) => !(r in visualContents))
    if (missing.length === 0) return
    let cancelled = false
    Promise.all(missing.map(async (ref) => {
      try {
        if (ref.startsWith('report:')) {
          const res = await loadReportObject(projectName, ref.slice('report:'.length))
          return [ref, res?.content ?? null] as const
        }
        const obj = visualRefToObjName(ref)
        if (!obj) return [ref, null] as const
        const res = await loadVisualObject(projectName, obj)
        return [ref, res?.content ?? null] as const
      } catch (err) {
        console.error('Failed to load tile content', ref, err)
        return [ref, null] as const
      }
    })).then((pairs) => {
      if (cancelled) return
      setVisualContents((prev) => {
        const next = { ...prev }
        for (const [ref, content] of pairs) next[ref] = content
        return next
      })
    })
    return () => { cancelled = true }
  }, [projectName, model.DASHBOARD.VISUALS, visualContents])

  // ── Mutators ────────────────────────────────────────────────
  const updateBody = useCallback((mutator: (b: DashboardBody) => void) => {
    setModel((prev) => {
      const next = JSON.parse(JSON.stringify(prev)) as DashboardModel
      mutator(next.DASHBOARD)
      return next
    })
  }, [])

  // A change the author may not have meant (arrange, remove, delete): say what
  // happened, with Undo. Stays 15 s (or until dismissed / the next note).
  const announce = useCallback((message: string) => setUndoNote(message), [])
  useEffect(() => {
    if (!undoNote) return
    const t = window.setTimeout(() => setUndoNote(null), 15000)
    return () => window.clearTimeout(t)
  }, [undoNote])

  const handleHeaderChange = useCallback(<K extends keyof DashboardBody>(field: K, value: DashboardBody[K]) => {
    updateBody((b) => { (b as DashboardBody)[field] = value })
  }, [updateBody])

  const select = useCallback((ids: string[], primary?: string | null) => {
    setSelectedIds(new Set(ids))
    setPrimaryId(primary !== undefined ? primary : ids.length === 1 ? ids[0] : ids[ids.length - 1] ?? null)
  }, [])

  const handleTilesChange = useCallback((tiles: DashboardVisual[]) => {
    updateBody((b) => { b.VISUALS = tiles })
  }, [updateBody])

  const handleDeleteTiles = useCallback((ids: string[]) => {
    if (!ids.length) return
    const gone = new Set(ids)
    updateBody((b) => {
      b.VISUALS = b.VISUALS.filter((v) => !gone.has(v.VISUAL_ID))
      for (const c of b.GLOBAL_CRITERIA) c.BROADCAST_TO = c.BROADCAST_TO.filter((id) => !gone.has(id))
      b.DRILL_THROUGH = b.DRILL_THROUGH.filter((d) => !gone.has(d.SOURCE_VISUAL_ID))
    })
    select([])
    setLOpen(new Set())
    setLFocus(null)
    announce(ids.length === 1 ? 'Tile removed from the dashboard.' : `${ids.length} tiles removed from the dashboard.`)
  }, [updateBody, select, announce])

  const handleVisualFieldChange = useCallback(<K extends keyof DashboardVisual>(
    visualId: string, field: K, value: DashboardVisual[K],
  ) => {
    updateBody((b) => {
      const v = b.VISUALS.find((x) => x.VISUAL_ID === visualId)
      if (!v) return
      const contentChanged =
        (field === 'VISUAL_CONFIG_REF' && value !== v.VISUAL_CONFIG_REF)
        || (field === 'REPORT_OBJ' && value !== v.REPORT_OBJ)
        || (field === 'TILE_KIND' && (value ?? 'visual') !== (v.TILE_KIND ?? 'visual'))
      if (contentChanged) {
        // The tile shows something else now — its bound variables may differ,
        // so drop stale bindings rather than silently hiding them.
        v.CRITERIA_BINDINGS = []
        b.GLOBAL_CRITERIA = syncBroadcastFromBindings(b).GLOBAL_CRITERIA
      }
      if (field === 'VISUAL_ID' && typeof value === 'string') {
        if (value !== v.VISUAL_ID && b.VISUALS.some((x) => x.VISUAL_ID === value)) {
          toast.error(`${value} is already used by another tile`)
          return
        }
        // Cascade the rename into broadcast targets and drill-through sources.
        const oldId = v.VISUAL_ID
        for (const c of b.GLOBAL_CRITERIA) c.BROADCAST_TO = c.BROADCAST_TO.map((id) => (id === oldId ? value : id))
        for (const d of b.DRILL_THROUGH) if (d.SOURCE_VISUAL_ID === oldId) d.SOURCE_VISUAL_ID = value
        setSelectedIds((prev) => (prev.has(oldId) ? new Set([...prev].map((x) => (x === oldId ? value : x))) : prev))
        setPrimaryId((cur) => (cur === oldId ? value : cur))
      }
      if (field === 'TILE_KIND' && value !== 'report') {
        delete v.TILE_KIND
        return
      }
      if (field === 'SHOW_HEADER' && value !== false) {
        delete v.SHOW_HEADER
        return
      }
      ;(v as Record<string, unknown>)[field as string] = value
    })
  }, [updateBody])

  const handleBoxChange = useCallback((id: string, field: keyof DashboardVisual['POSITION'], value: number) => {
    updateBody((b) => { b.VISUALS = setTileBox(b.VISUALS, id, field, value) })
  }, [updateBody])

  const handleSetBinding = useCallback((visualId: string, visualParam: string, binding: DashboardCriterionBinding | null) => {
    updateBody((b) => {
      const v = b.VISUALS.find((x) => x.VISUAL_ID === visualId)
      if (!v) return
      const list = Array.isArray(v.CRITERIA_BINDINGS) ? [...v.CRITERIA_BINDINGS] : []
      const idx = list.findIndex((x) => x.VISUAL_PARAM === visualParam)
      if (binding === null) { if (idx >= 0) list.splice(idx, 1) }
      else if (idx >= 0) list[idx] = binding
      else list.push(binding)
      v.CRITERIA_BINDINGS = list
      // Keep BROADCAST_TO consistent so the runtime sees the same edges.
      b.GLOBAL_CRITERIA = syncBroadcastFromBindings(b).GLOBAL_CRITERIA
    })
  }, [updateBody])

  // Add / replace from the modal.
  const handlePick = useCallback((pick: AddVisualPick) => {
    const m = modal
    setModal(null)
    if (!m) return
    if (m.mode === 'replace' && primaryId) {
      const id = primaryId
      updateBody((b) => {
        const v = b.VISUALS.find((x) => x.VISUAL_ID === id)
        if (!v) return
        if (pick.kind === 'report') {
          v.TILE_KIND = 'report'
          v.REPORT_OBJ = pick.ref
        } else {
          delete v.TILE_KIND
          delete v.REPORT_OBJ
          v.VISUAL_CONFIG_REF = pick.ref
        }
        v.CRITERIA_BINDINGS = []
        b.GLOBAL_CRITERIA = syncBroadcastFromBindings(b).GLOBAL_CRITERIA
        // A tile still carrying its default name takes the new visual's.
        if (!v.VISUAL_NAME || v.VISUAL_NAME === 'New Tile') v.VISUAL_NAME = pick.title
      })
      return
    }
    // The new tile is built from the model in hand so its id is known now.
    const tile = blankVisual(model.DASHBOARD.VISUALS)
    tile.VISUAL_NAME = pick.title
    tile.POSITION.Y = bottomRow(model.DASHBOARD.VISUALS)
    if (pick.kind === 'report') { tile.TILE_KIND = 'report'; tile.REPORT_OBJ = pick.ref }
    else tile.VISUAL_CONFIG_REF = pick.ref
    updateBody((b) => {
      const t = { ...tile, POSITION: { ...tile.POSITION } }
      if (b.VISUALS.some((v) => v.VISUAL_ID === t.VISUAL_ID)) t.VISUAL_ID = blankVisual(b.VISUALS).VISUAL_ID
      b.VISUALS.push(t)
    })
    select([tile.VISUAL_ID])
    setTab('layout')
  }, [modal, primaryId, model.DASHBOARD.VISUALS, updateBody, select])

  const handleAddEmpty = useCallback(() => {
    setModal(null)
    const tile = blankVisual(model.DASHBOARD.VISUALS)
    tile.POSITION.Y = bottomRow(model.DASHBOARD.VISUALS)
    updateBody((b) => { b.VISUALS.push({ ...tile, POSITION: { ...tile.POSITION } }) })
    select([tile.VISUAL_ID])
    setTab('layout')
  }, [model.DASHBOARD.VISUALS, updateBody, select])

  const handleArrange = useCallback((mode: ArrangeMode) => {
    setArrangeOpen(false)
    updateBody((b) => { b.VISUALS = arrangeTiles(b.VISUALS, mode) })
    announce(ARRANGE_DONE[mode])
  }, [updateBody, announce])

  const handleRemoveBand = useCallback((from: number, to: number) => {
    updateBody((b) => { b.VISUALS = removeBand(b.VISUALS, from, to) })
    announce('Empty space removed. Tiles below moved up.')
  }, [updateBody, announce])

  // ── Global criteria ─────────────────────────────────────────
  const handleAddCriterion = useCallback(() => {
    const idx = model.DASHBOARD.GLOBAL_CRITERIA.length
    updateBody((b) => { b.GLOBAL_CRITERIA.push(blankCriterion()) })
    setGOpen((s) => new Set([...s, idx]))
    setGFocus(idx)
  }, [model.DASHBOARD.GLOBAL_CRITERIA.length, updateBody])

  const handleDeleteCriterion = useCallback((idx: number) => {
    updateBody((b) => {
      const removed = b.GLOBAL_CRITERIA[idx]
      b.GLOBAL_CRITERIA.splice(idx, 1)
      if (removed?.PARAM_NAME) {
        for (const v of b.VISUALS) {
          if (!v.CRITERIA_BINDINGS) continue
          v.CRITERIA_BINDINGS = v.CRITERIA_BINDINGS.filter(
            (bnd) => !(bnd.BIND_TYPE === 'global' && bnd.GLOBAL_PARAM === removed.PARAM_NAME),
          )
        }
      }
    })
    // Indices after the removed card shift down by one.
    setGOpen((s) => new Set([...s].filter((i) => i !== idx).map((i) => (i > idx ? i - 1 : i))))
    setGFocus(null)
    announce('Criterion removed.')
  }, [updateBody, announce])

  const handleCriterionChange = useCallback(<K extends keyof DashboardGlobalCriterion>(
    idx: number, field: K, value: DashboardGlobalCriterion[K],
  ) => {
    setGFocus(idx)
    updateBody((b) => {
      const c = b.GLOBAL_CRITERIA[idx]
      if (!c) return
      // Cascade PARAM_NAME renames into tile bindings so they stay wired.
      if (field === 'PARAM_NAME' && typeof value === 'string') {
        const oldName = c.PARAM_NAME
        if (oldName && oldName !== value) {
          for (const v of b.VISUALS) {
            for (const bnd of v.CRITERIA_BINDINGS || []) {
              if (bnd.BIND_TYPE === 'global' && bnd.GLOBAL_PARAM === oldName) bnd.GLOBAL_PARAM = value
            }
          }
        }
      }
      if (value === undefined) delete (c as Record<string, unknown>)[field as string]
      else (c as Record<string, unknown>)[field as string] = value
    })
  }, [updateBody])

  const handleToggleBroadcast = useCallback((idx: number, visualId: string) => {
    setGFocus(idx)
    updateBody((b) => {
      const c = b.GLOBAL_CRITERIA[idx]
      if (!c) return
      const i = c.BROADCAST_TO.indexOf(visualId)
      if (i >= 0) c.BROADCAST_TO.splice(i, 1)
      else c.BROADCAST_TO.push(visualId)
    })
  }, [updateBody])

  const toggleCard = (set: typeof setGOpen, focus: typeof setGFocus) => (idx: number) => {
    set((s) => {
      const n = new Set(s)
      if (n.has(idx)) n.delete(idx)
      else n.add(idx)
      return n
    })
    focus(idx)
  }

  // ── Drill-through ───────────────────────────────────────────
  const handleAddDrill = useCallback(() => {
    const idx = model.DASHBOARD.DRILL_THROUGH.length
    const source = selectedIds.size === 1 ? [...selectedIds][0] : ''
    updateBody((b) => {
      const d = blankDrillThrough()
      d.SOURCE_VISUAL_ID = source
      b.DRILL_THROUGH.push(d)
    })
    setLOpen((s) => new Set([...s, idx]))
    setLFocus(idx)
  }, [model.DASHBOARD.DRILL_THROUGH.length, selectedIds, updateBody])

  const handleDeleteDrill = useCallback((idx: number) => {
    updateBody((b) => { b.DRILL_THROUGH.splice(idx, 1) })
    setLOpen((s) => new Set([...s].filter((i) => i !== idx).map((i) => (i > idx ? i - 1 : i))))
    setLFocus(null)
    announce('Drill-through removed.')
  }, [updateBody, announce])

  const handleDrillFieldChange = useCallback(<K extends keyof DashboardDrillThrough>(
    idx: number, field: K, value: DashboardDrillThrough[K],
  ) => {
    setLFocus(idx)
    updateBody((b) => {
      const d = b.DRILL_THROUGH[idx]
      if (!d) return
      if (value === undefined || (field === 'SOURCE_COLUMN' && value === '')) delete (d as Record<string, unknown>)[field as string]
      else (d as Record<string, unknown>)[field as string] = value
    })
  }, [updateBody])

  const handleDrillMappingChange = useCallback((idx: number, oldKey: string, newKey: string, newValue: string) => {
    setLFocus(idx)
    updateBody((b) => {
      const d = b.DRILL_THROUGH[idx]
      if (!d) return
      const next: Record<string, string> = {}
      for (const [k, v] of Object.entries(d.PARAM_MAPPING)) {
        if (k === oldKey) { if (newKey) next[newKey] = newValue }
        else next[k] = v
      }
      d.PARAM_MAPPING = next
    })
  }, [updateBody])

  // `source` adds that exact source (a clicked-column one); none = a new
  // row-column placeholder.
  const handleAddDrillMapping = useCallback((idx: number, source?: string) => {
    updateBody((b) => {
      const d = b.DRILL_THROUGH[idx]
      if (!d) return
      if (source) {
        if (source in d.PARAM_MAPPING) return
        d.PARAM_MAPPING = { ...d.PARAM_MAPPING, [source]: '' }
        return
      }
      let n = 1
      let key = `SOURCE_COLUMN_${n}`
      while (key in d.PARAM_MAPPING) { n += 1; key = `SOURCE_COLUMN_${n}` }
      d.PARAM_MAPPING = { ...d.PARAM_MAPPING, [key]: '' }
    })
  }, [updateBody])

  const handleDeleteDrillMapping = useCallback((idx: number, key: string) => {
    updateBody((b) => {
      const d = b.DRILL_THROUGH[idx]
      if (!d) return
      const next = { ...d.PARAM_MAPPING }
      delete next[key]
      d.PARAM_MAPPING = next
    })
  }, [updateBody])

  // ── Selection on the canvas ─────────────────────────────────
  // On the Drill-through tab a click on a tile makes it the open link's
  // source; on any other tab it selects the tile (and shows the Layout tab).
  const handleCanvasSelect = useCallback((id: string | null, mode: 'single' | 'toggle' | 'keep' = 'single') => {
    if (!id) { select([]); return }
    if (tab === 'drill' && lFocus != null && lOpen.has(lFocus) && mode === 'single' && canEdit) {
      const d = model.DASHBOARD.DRILL_THROUGH[lFocus]
      if (d && d.SOURCE_VISUAL_ID !== id) {
        const at = lFocus
        updateBody((b) => {
          const x = b.DRILL_THROUGH[at]
          if (!x) return
          x.SOURCE_VISUAL_ID = id
          delete x.SOURCE_COLUMN
        })
      }
      return
    }
    if (tab !== 'layout') setTab('layout')
    if (mode === 'toggle') {
      const next = new Set(selectedIds)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      setSelectedIds(next)
      setPrimaryId(next.has(id) ? id : [...next].pop() ?? null)
      return
    }
    if (mode === 'keep' && selectedIds.has(id)) { setPrimaryId(id); return }
    select([id])
  }, [tab, lFocus, lOpen, canEdit, model.DASHBOARD.DRILL_THROUGH, updateBody, selectedIds, select])

  // Keyboard on the layout: arrows nudge (Shift = a whole cell), Delete removes
  // the selection, Escape clears it. Skipped while typing in a field.
  useEffect(() => {
    if (tab !== 'layout' || selectedIds.size === 0 || modal || editVisualObj || previewOpen) return
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return
      if (e.key === 'Escape') { select([]); return }
      if (!canEdit) return
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault()
        handleDeleteTiles([...selectedIds])
        return
      }
      const step = e.shiftKey ? 1 : DASH_STEP
      const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key]
      if (!d) return
      e.preventDefault()
      const ids = [...selectedIds]
      updateBody((b) => { b.VISUALS = moveTiles(b.VISUALS, ids, d[0], d[1]) })
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [tab, selectedIds, modal, editVisualObj, previewOpen, canEdit, select, handleDeleteTiles, updateBody])

  // Close the Arrange menu on an outside press.
  useEffect(() => {
    if (!arrangeOpen) return
    const onDown = (e: PointerEvent) => {
      if (arrangeRef.current && !arrangeRef.current.contains(e.target as Node)) setArrangeOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setArrangeOpen(false) }
    document.addEventListener('pointerdown', onDown, true)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown, true)
      document.removeEventListener('keydown', onKey)
    }
  }, [arrangeOpen])

  // ── Find a tile ─────────────────────────────────────────────
  const tileRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const registerTileRef = useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) tileRefs.current.set(id, el)
    else tileRefs.current.delete(id)
  }, [])
  const findMatches = useMemo(() => {
    const q = findQ.trim().toLowerCase()
    if (!q) return [] as string[]
    return model.DASHBOARD.VISUALS
      .filter((v) => v.VISUAL_ID.toLowerCase().includes(q) || (v.VISUAL_NAME || '').toLowerCase().includes(q)
        || (v.VISUAL_CONFIG_REF || '').toLowerCase().includes(q) || (v.REPORT_OBJ || '').toLowerCase().includes(q))
      .map((v) => v.VISUAL_ID)
  }, [findQ, model.DASHBOARD.VISUALS])
  useEffect(() => { setFindI(-1) }, [findQ])
  const goToMatch = useCallback((i: number) => {
    if (!findMatches.length) return
    const n = ((i % findMatches.length) + findMatches.length) % findMatches.length
    setFindI(n)
    const id = findMatches[n]
    setTab('layout')
    select([id])
    requestAnimationFrame(() => {
      tileRefs.current.get(id)?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
    })
  }, [findMatches, select])
  // Ctrl/⌘+F inside the designer focuses the find box.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f' && !modal && !editVisualObj && !previewOpen) {
        e.preventDefault()
        findRef.current?.focus()
        findRef.current?.select()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [modal, editVisualObj, previewOpen])

  // A newly added tile scrolls into view.
  const prevCount = useRef(model.DASHBOARD.VISUALS.length)
  useEffect(() => {
    const prev = prevCount.current
    prevCount.current = model.DASHBOARD.VISUALS.length
    if (model.DASHBOARD.VISUALS.length > prev && primaryId) {
      const id = primaryId
      requestAnimationFrame(() => tileRefs.current.get(id)?.scrollIntoView({ behavior: 'smooth', block: 'center' }))
    }
  }, [model.DASHBOARD.VISUALS.length, primaryId])

  // ── Save ────────────────────────────────────────────────────
  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!projectName || !objectName) return false
    setSaving(true)
    try {
      await saveDashboardObject(projectName, objectName, model)
      setSavedSnapshot(JSON.stringify(model))
      toast.success('Dashboard saved')
      onSaved?.()
      return true
    } catch (err) {
      console.error('Save failed', err)
      toast.error('Failed to save dashboard')
      return false
    } finally {
      setSaving(false)
    }
  }, [projectName, objectName, model, onSaved])

  useEffect(() => {
    if (!embedded || !onDirtyChange) return
    onDirtyChange(isDirty)
    return () => onDirtyChange(false)
  }, [embedded, isDirty, onDirtyChange])
  useEffect(() => {
    if (!embedded || !registerSave) return
    registerSave(handleSave)
  }, [embedded, registerSave, handleSave])

  const handleBack = useCallback(() => {
    const selectedNodeId = (location.state as { selectedNodeId?: string })?.selectedNodeId
    guard(() => navigate('/workbench', { state: { selectedNodeId } }))
  }, [guard, location.state, navigate])

  const handleSaveAndContinue = useCallback(async () => {
    const ok = await handleSave()
    if (ok) confirmDiscard()
  }, [handleSave, confirmDiscard])

  const panelMax = useCallback(() => Math.max(340, Math.min(640, window.innerWidth - 560)), [])
  const panel = useDragWidth('panelWidth', 420, 320, panelMax, 'Panel width', {
    fromRight: true, prefix: 'twasta.dashboardDesigner.',
  })

  // ── Derived ─────────────────────────────────────────────────
  const body = model.DASHBOARD
  const primary = useMemo(
    () => (selectedIds.size === 1 && primaryId ? body.VISUALS.find((v) => v.VISUAL_ID === primaryId) || null : null),
    [selectedIds, primaryId, body.VISUALS],
  )
  const usedVisualRefs = useMemo(() => new Set(body.VISUALS.map((v) => v.VISUAL_CONFIG_REF).filter(Boolean)), [body.VISUALS])
  const usedReports = useMemo(
    () => new Set(body.VISUALS.map((v) => (v.TILE_KIND === 'report' ? v.REPORT_OBJ || '' : '')).filter(Boolean)),
    [body.VISUALS],
  )
  const focusedCriterion = gFocus != null && gOpen.has(gFocus) ? body.GLOBAL_CRITERIA[gFocus] ?? null : null
  const focusedDrill = lFocus != null ? body.DRILL_THROUGH[lFocus] ?? null : null
  const findCurrent = findI >= 0 ? findMatches[findI] ?? null : null

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: '100%' }}>
        <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: 8 }}>Loading dashboard…</span>
      </div>
    )
  }

  const TABS: Array<{ key: PanelTab; label: string; n?: number }> = [
    { key: 'layout', label: 'Layout', n: body.VISUALS.length },
    { key: 'criteria', label: 'Global criteria', n: body.GLOBAL_CRITERIA.length },
    { key: 'drill', label: 'Drill-through', n: body.DRILL_THROUGH.length },
  ]

  return (
    <div
      className={`vdo-root${panel.dragging ? ' vdo-resizing' : ''}`}
      style={embedded && embedFullscreen ? { position: 'fixed', inset: 0, zIndex: 1000 } : undefined}
    >
      <style>{VDO_CSS}</style>
      <style>{DD_CSS}</style>

      {/* ── Top bar: name · layout toolbar · preview + save ── */}
      <div className="vdo-top">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '1 1 260px', minWidth: 0 }}>
          {!embedded && (
            <button className="vdo-ib vdo-float" style={{ borderRadius: 11 }} onClick={handleBack}
              title="Back to workbench" aria-label="Back to workbench">
              <ArrowLeft size={16} />
            </button>
          )}
          <TypeChip code="D" size={22} />
          <div style={{ minWidth: 0, flex: '1 1 auto' }}>
            <div className="dd-ell" style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>
              {projectName} / <span className="dd-mono">{objectName}</span> · dashboard
            </div>
            <input
              aria-label="Dashboard name"
              value={body.DASHBOARD_NAME}
              disabled={!canEdit}
              onChange={(e) => handleHeaderChange('DASHBOARD_NAME', e.target.value)}
              placeholder="Dashboard name"
              style={{
                display: 'block', width: '100%', maxWidth: 420, border: '1px solid transparent', borderRadius: 8,
                padding: '1px 6px', marginLeft: -7, background: 'transparent', fontSize: 17, fontWeight: 650,
                color: 'var(--color-text)', outline: 'none',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)' }}
              onBlur={(e) => { e.currentTarget.style.borderColor = 'transparent' }}
            />
          </div>
        </div>

        <div className="vdo-float vdo-toolbar" role="toolbar" aria-label="Dashboard layout">
          {canEdit && (
            <button className="dd-tb pri" onClick={() => setModal({ mode: 'add', kind: 'visual' })}>
              <Plus size={17} /> Add visual
            </button>
          )}
          {canEdit && (
            <div ref={arrangeRef} style={{ position: 'relative' }}>
              <button className="dd-tb" onClick={() => setArrangeOpen((o) => !o)} aria-expanded={arrangeOpen}
                aria-haspopup="menu" disabled={body.VISUALS.length === 0}>
                <LayoutGrid size={17} /> Arrange <ChevronDown size={14} />
              </button>
              {arrangeOpen && (
                <div className="dd-menu" role="menu" aria-label="Arrange tiles automatically">
                  <div className="dd-menu-h">Arrange automatically</div>
                  {ARRANGE.map((a) => (
                    <button key={a.mode} className="dd-mi" role="menuitem" onClick={() => handleArrange(a.mode)}>
                      <span className="ic" aria-hidden="true">
                        {a.icon.map((span, i) => <i key={i} style={{ gridColumn: `span ${span}` }} />)}
                      </span>
                      <span><b>{a.title}</b><span className="d">{a.detail}</span></span>
                    </button>
                  ))}
                  <div className="dd-menu-f">You can undo any of these.</div>
                </div>
              )}
            </div>
          )}
          {canEdit && <span className="vdo-sep" />}
          <div className={`dd-find${findQ.trim() ? ' q' : ''}`} role="search">
            <Search size={16} style={{ color: 'var(--color-text-faint)', flexShrink: 0 }} />
            <input
              ref={findRef}
              aria-label="Find a visual on this dashboard"
              placeholder="Find a visual on this dashboard"
              value={findQ}
              onChange={(e) => setFindQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') { setFindQ(''); e.currentTarget.blur(); return }
                if (!findMatches.length) return
                if (e.key === 'Enter' || e.key === 'ArrowDown') { e.preventDefault(); goToMatch(findI < 0 ? 0 : findI + (e.shiftKey ? -1 : 1)) }
                else if (e.key === 'ArrowUp') { e.preventDefault(); goToMatch(findI < 0 ? findMatches.length - 1 : findI - 1) }
              }}
            />
            {findQ.trim() && (
              <>
                <span className={`cnt${findMatches.length ? '' : ' no'}`} aria-live="polite">
                  {findMatches.length === 0 ? 'No matches'
                    : findI < 0 ? `${findMatches.length} found` : `${findI + 1} of ${findMatches.length}`}
                </span>
                <button className="vdo-ib sm" aria-label="Previous match" disabled={!findMatches.length}
                  onClick={() => goToMatch(findI < 0 ? findMatches.length - 1 : findI - 1)}><ChevronUp size={15} /></button>
                <button className="vdo-ib sm" aria-label="Next match" disabled={!findMatches.length}
                  onClick={() => goToMatch(findI < 0 ? 0 : findI + 1)}><ChevronDown size={15} /></button>
                <button className="vdo-ib sm" aria-label="Clear search" onClick={() => setFindQ('')}><X size={14} /></button>
              </>
            )}
          </div>
          {canEdit && <span className="vdo-sep" />}
          {canEdit && <UndoRedoButtons history={history} />}
        </div>

        {/* Never shrinks below its buttons: when the bar runs out of room it
            wraps to its own line instead of sliding under the toolbar. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: '0 0 auto', marginLeft: 'auto', justifyContent: 'flex-end' }}>
          {canEdit && (
            <button className="vdo-ghost" onClick={() => setMenuOpen(true)} title="Put this dashboard in the application's menu">
              Add to menu…
            </button>
          )}
          <button className="vdo-ghost" onClick={() => setPreviewOpen(true)} title="Open the dashboard with live data">
            <Eye size={16} /> Preview
          </button>
          {embedded && !hostFullscreen && (
            <button className="vdo-ib" onClick={() => setEmbedFullscreen((f) => !f)}
              title={embedFullscreen ? 'Restore' : 'Full screen'} aria-label={embedFullscreen ? 'Restore' : 'Full screen'}>
              {embedFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
          )}
          {canEdit ? (
            <button className="vdo-save" onClick={handleSave} disabled={saving}
              title={isDirty ? 'You have unsaved changes' : 'No changes to save'}>
              {saving ? <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={15} />}
              {saving ? 'Saving…' : 'Save'}
              {isDirty && !saving && <span className="dirty" aria-label="Unsaved changes" />}
            </button>
          ) : (
            <ReadOnlyBadge />
          )}
        </div>
      </div>

      <div className="vdo-body">
        <DesignCanvas
          project={projectName}
          objectName={objectName}
          model={model}
          canEdit={canEdit}
          tab={tab}
          selectedIds={selectedIds}
          visualContents={visualContents}
          appColor={appColor}
          findQuery={findQ}
          findMatches={findMatches}
          findCurrent={findCurrent}
          focusedCriterion={focusedCriterion}
          focusedDrill={focusedDrill}
          onSelect={handleCanvasSelect}
          onTilesChange={handleTilesChange}
          onRemoveBand={handleRemoveBand}
          onOpenAdd={() => setModal({ mode: 'add', kind: 'visual' })}
          onPickCriterion={(name) => {
            const idx = body.GLOBAL_CRITERIA.findIndex((c) => c.PARAM_NAME === name)
            if (idx < 0) return
            setTab('criteria')
            setGOpen((s) => new Set([...s, idx]))
            setGFocus(idx)
          }}
          registerTileRef={registerTileRef}
          dataVersion={dataVersion + agentRefresh}
          toast={undoNote ? {
            message: undoNote,
            onUndo: () => { history.undo(); setUndoNote(null) },
            onDismiss: () => setUndoNote(null),
          } : null}
        />

        <div className={`dd-grip${panel.dragging ? ' on' : ''}`} {...panel.handle} />

        <aside className="vdo-float dd-panel" style={{ width: panel.width }} aria-label="Dashboard panel">
          <div style={{ padding: '12px 12px 10px', flexShrink: 0 }}>
            <div className="dd-ptabs" role="tablist">
              {TABS.map((t) => (
                <button key={t.key} className="dd-pt" role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}>
                  {t.label}{t.n ? <span className="n">{t.n}</span> : null}
                </button>
              ))}
              <button className="dd-pt icon" role="tab" aria-selected={tab === 'settings'} onClick={() => setTab('settings')}
                aria-label="Dashboard properties" title="Dashboard properties">
                <Settings size={16} />
              </button>
            </div>
          </div>
          <div className="dd-pscroll">
            {tab === 'layout' && (
              <LayoutPanel
                canEdit={canEdit}
                tiles={body.VISUALS}
                selectedIds={selectedIds}
                primary={primary}
                projectVisuals={projectVisuals}
                projectReports={projectReports}
                visualContent={primary ? visualContents[tileContentKey(primary)] : undefined}
                globalCriteria={body.GLOBAL_CRITERIA}
                onOpenAdd={() => setModal({ mode: 'add', kind: 'visual' })}
                onOpenReplace={(kind) => setModal({ mode: 'replace', kind })}
                onSetContentKind={(kind) => {
                  if (!primary) return
                  const was = primary.TILE_KIND === 'report' ? 'report' : 'visual'
                  if (kind === was) return
                  handleVisualFieldChange(primary.VISUAL_ID, 'TILE_KIND', kind === 'report' ? 'report' : undefined)
                  const hasRef = kind === 'report' ? !!primary.REPORT_OBJ : !!primary.VISUAL_CONFIG_REF
                  if (!hasRef) setModal({ mode: 'replace', kind })
                }}
                onEditVisual={(obj) => setEditVisualObj(obj)}
                onFieldChange={handleVisualFieldChange}
                onBoxChange={handleBoxChange}
                onSetBinding={handleSetBinding}
                onDelete={handleDeleteTiles}
                onClearSelection={() => select([])}
                onGoToCriteria={() => setTab('criteria')}
              />
            )}
            {tab === 'criteria' && (
              <CriteriaPanel
                canEdit={canEdit}
                project={projectName}
                projectVars={projectVars}
                body={body}
                open={gOpen}
                focused={gFocus}
                onToggle={toggleCard(setGOpen, setGFocus)}
                onToggleAll={() => {
                  const all = body.GLOBAL_CRITERIA.every((_, i) => gOpen.has(i))
                  setGOpen(all ? new Set() : new Set(body.GLOBAL_CRITERIA.map((_, i) => i)))
                }}
                onAdd={handleAddCriterion}
                onDelete={handleDeleteCriterion}
                onChange={handleCriterionChange}
                onToggleBroadcast={handleToggleBroadcast}
              />
            )}
            {tab === 'drill' && (
              <DrillPanel
                canEdit={canEdit}
                project={projectName}
                body={body}
                projectVisuals={projectVisuals}
                projectDashboards={projectDashboards.filter((d) => d.obj_name !== objectName)}
                visualContents={visualContents}
                open={lOpen}
                focused={lFocus}
                onToggle={toggleCard(setLOpen, setLFocus)}
                onToggleAll={() => {
                  const all = body.DRILL_THROUGH.every((_, i) => lOpen.has(i))
                  setLOpen(all ? new Set() : new Set(body.DRILL_THROUGH.map((_, i) => i)))
                }}
                onAdd={handleAddDrill}
                onDelete={handleDeleteDrill}
                onFieldChange={handleDrillFieldChange}
                onMappingChange={handleDrillMappingChange}
                onAddMapping={handleAddDrillMapping}
                onDeleteMapping={handleDeleteDrillMapping}
              />
            )}
            {tab === 'settings' && (
              <PropertiesPanel body={body} objectName={objectName} canEdit={canEdit} onChange={handleHeaderChange} />
            )}
          </div>
        </aside>
      </div>

      {modal && (
        <AddVisualModal
          mode={modal.mode}
          initialKind={modal.kind}
          projectVisuals={projectVisuals}
          projectReports={projectReports}
          usedVisualRefs={usedVisualRefs}
          usedReports={usedReports}
          onPick={handlePick}
          onAddEmpty={handleAddEmpty}
          onClose={() => setModal(null)}
        />
      )}

      <PublishToMenuDialog
        open={menuOpen}
        mode="menu"
        project={projectName}
        targets={menuOpen ? [{ obj_name: objectName, obj_type: 'D', title: body.DASHBOARD_NAME || objectName }] : []}
        onClose={() => setMenuOpen(false)}
        onPublished={() => setMenuOpen(false)}
      />

      <DiscardChangesModal
        open={showDiscardModal}
        onCancel={cancelDiscard}
        onDiscard={confirmDiscard}
        onSave={handleSaveAndContinue}
        saving={saving}
      />

      <DashboardPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        projectName={projectName}
        objectName={objectName}
        model={model}
      />

      {/* Edit-visual overlay — the FULL Visual designer for a tile's visual,
          without leaving the dashboard. On Done the tile's cached preview and
          loaded model are dropped so the edits show at once. */}
      {editVisualObj && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--color-bg)', display: 'flex', flexDirection: 'column' }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px',
            borderBottom: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)',
          }}>
            <div className="flex items-center gap-2" style={{ fontSize: 14, fontWeight: 600 }}>
              <Pencil size={15} style={{ color: 'var(--color-accent)' }} />
              Editing visual · {editVisualObj}
            </div>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                const obj = editVisualObj
                setEditVisualObj(null)
                if (obj) {
                  bustPreview(projectName, 'V', obj)
                  setDataVersion((v) => v + 1)
                  setVisualContents((prev) => {
                    const next = { ...prev }
                    for (const key of Object.keys(next)) if (visualRefToObjName(key) === obj) delete next[key]
                    return next
                  })
                }
              }}
              title="Close the visual editor and return to the dashboard"
            >
              <X size={14} /> Done
            </button>
          </div>
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <VisualDesignerPage key={`${projectName}:${editVisualObj}`} embedded embedProject={projectName} embedObjName={editVisualObj} />
          </div>
        </div>
      )}
    </div>
  )
}
